import React, { useState } from 'react';
import { 
  Building2, Send, CheckCircle, Users, Globe, DollarSign, 
  Headphones, Award, ArrowRight, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';

const ResellerApplication = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    country: '',
    expected_clients: '',
    target_market: '',
    experience: '',
    why_partner: ''
  });

  const benefits = [
    { icon: DollarSign, title: 'Generous Revenue Share', description: 'Earn up to 60% of client subscriptions with our tiered revenue model' },
    { icon: Building2, title: 'White-Label Platform', description: 'Your branding, your domain - clients see you as the provider' },
    { icon: Award, title: 'Free Enterprise Website', description: 'Unlock a free enterprise site for your agency after 5 client sales' },
    { icon: Globe, title: 'Unlimited Sub-Tenants', description: 'No limits on how many clients you can onboard' },
    { icon: Headphones, title: 'Dedicated Support', description: 'Direct access to our team for technical issues' },
    { icon: Sparkles, title: 'All Premium Features', description: 'AI, analytics, SSO, API access - everything included' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const required = ['company_name', 'contact_name', 'email', 'country', 'expected_clients', 'target_market'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await api.post('/public/reseller/apply', formData);
      setApplicationId(response.data.application_id);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl p-8 max-w-lg text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-600" size={40} />
          </div>
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Application Submitted!
          </h1>
          <p className="text-slate-600 mb-6">
            Thank you for your interest in becoming a Dynatexsaas reseller partner. 
            We'll review your application and get back to you within 2-3 business days.
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-500">Your application ID:</p>
            <code className="text-indigo-600 font-mono">{applicationId}</code>
          </div>
          <Button onClick={() => window.location.href = '/'}>
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <span className="text-white font-bold text-xl">NexusCMS</span>
          </div>
          <Button variant="outline" className="text-white border-white/20 hover:bg-white/10" onClick={() => window.location.href = '/login'}>
            Sign In
          </Button>
        </div>
      </div>

      {/* Hero */}
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
          <Building2 className="text-purple-400" size={18} />
          <span className="text-purple-200 text-sm">Partner Program</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Become a Reseller Partner
        </h1>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto">
          White-label our platform, earn generous commissions, and grow your business 
          by offering premium CMS solutions to your clients.
        </p>
      </div>

      {/* Benefits */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="text-white" size={24} />
                </div>
                <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </div>
            );
          })}
        </div>

        {/* Revenue Tiers */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-white text-center mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Revenue Share Tiers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <p className="text-slate-400 mb-2">Monthly Revenue $0 - $5,000</p>
              <p className="text-4xl font-bold text-white mb-2">40%</p>
              <p className="text-purple-400 text-sm">You keep</p>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-xl border border-purple-500/30">
              <p className="text-slate-300 mb-2">Monthly Revenue $5,001 - $20,000</p>
              <p className="text-4xl font-bold text-white mb-2">50%</p>
              <p className="text-purple-400 text-sm">You keep</p>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <p className="text-slate-400 mb-2">Monthly Revenue $20,001+</p>
              <p className="text-4xl font-bold text-white mb-2">60%</p>
              <p className="text-purple-400 text-sm">You keep</p>
            </div>
          </div>
          <p className="text-center text-slate-400 text-sm mt-6">
            * Custom revenue arrangements available for high-volume partners
          </p>
        </div>

        {/* Application Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <h2 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Apply to Become a Partner
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Your Agency Name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Your Name"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United States"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Expected Number of Clients *</Label>
                  <Select 
                    value={formData.expected_clients} 
                    onValueChange={(v) => setFormData({ ...formData, expected_clients: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 clients</SelectItem>
                      <SelectItem value="11-25">11-25 clients</SelectItem>
                      <SelectItem value="26-50">26-50 clients</SelectItem>
                      <SelectItem value="51-100">51-100 clients</SelectItem>
                      <SelectItem value="100+">100+ clients</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Target Market *</Label>
                  <Select 
                    value={formData.target_market} 
                    onValueChange={(v) => setFormData({ ...formData, target_market: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select market" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Schools & Education">Schools & Education</SelectItem>
                      <SelectItem value="Small Business">Small Business</SelectItem>
                      <SelectItem value="Enterprise">Enterprise</SelectItem>
                      <SelectItem value="Non-Profit">Non-Profit</SelectItem>
                      <SelectItem value="Government">Government</SelectItem>
                      <SelectItem value="Mixed">Mixed / Multiple</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Relevant Experience</Label>
                <Textarea
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  placeholder="Tell us about your experience with CMS platforms, web development, or selling SaaS products..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Why do you want to partner with us?</Label>
                <Textarea
                  value={formData.why_partner}
                  onChange={(e) => setFormData({ ...formData, why_partner: e.target.value })}
                  placeholder="What attracted you to NexusCMS? How do you plan to sell it to your clients?"
                  rows={3}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                disabled={submitting}
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <>
                    Submit Application
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>

              <p className="text-center text-slate-500 text-sm">
                We review all applications within 2-3 business days. 
                You'll receive an email with the next steps.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="container mx-auto px-4 py-12 text-center">
        <p className="text-slate-400">
          © {new Date().getFullYear()} Dynatexsaas. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ResellerApplication;
